import RestAPIService from "./RestAPIService";
import { getMaxDocUploadSizeBytes, getMaxDocUploadSizeMb } from "../../Components/CommonFunctions/CommonFunctions";

const S3_PRESIGNED_UPLOAD_URL = "core/s3/presigned-upload/";

const ALLOWED_FILE_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/bmp",
    "image/webp",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
];

/**
 * S3UploadService - Common utility for direct-to-S3 file uploads via presigned URLs.
 *
 * Usage:
 *   import S3UploadService from "../../Utility/Services/S3UploadService";
 *
 *   // Upload a single file
 *   const { key } = await S3UploadService.upload(file, "patient/123/", "PTDOC_");
 *
 *   // Upload multiple files
 *   const results = await S3UploadService.uploadMultiple(fileList, "patient/123/", "PTLAB_");
 *
 *   // Upload a blob (e.g., from webcam capture)
 *   const { key } = await S3UploadService.uploadBlob(blob, "capture.jpg", "image/jpeg", "patient/photos123/", "PPH_");
 */
class S3UploadService {

    /**
     * Upload a single File object directly to S3 via presigned URL.
     */
    upload = async (file, folderPath, filenamePrefix = "", onProgress = null) => {
        this._validateFile(file, file.name, file.size, file.type);

        const presignedData = await this._getPresignedUrl({
            file_name: file.name,
            content_type: file.type,
            file_size: file.size,
            folder_path: folderPath,
            filename_prefix: filenamePrefix,
        });

        await this._uploadToS3(presignedData.url, file, onProgress);

        return { key: presignedData.key };
    };

    /**
     * Upload multiple files directly to S3.
     */
    uploadMultiple = async (files, folderPath, filenamePrefix = "", onFileProgress = null) => {
        const results = [];
        for (let i = 0; i < files.length; i++) {
            const progressCb = onFileProgress ? (percent) => onFileProgress(i, percent) : null;
            const result = await this.upload(files[i], folderPath, filenamePrefix, progressCb);
            results.push(result);
        }
        return results;
    };

    /**
     * Upload a Blob (e.g., from webcam capture or canvas) directly to S3.
     */
    uploadBlob = async (blob, fileName, contentType, folderPath, filenamePrefix = "", onProgress = null) => {
        this._validateFile(blob, fileName, blob.size, contentType);

        const presignedData = await this._getPresignedUrl({
            file_name: fileName,
            content_type: contentType,
            file_size: blob.size,
            folder_path: folderPath,
            filename_prefix: filenamePrefix,
        });

        await this._uploadToS3(presignedData.url, blob, onProgress);

        return { key: presignedData.key };
    };

    /**
     * Get a presigned upload URL from the backend.
     */
    _getPresignedUrl = async (params) => {
        const response = await RestAPIService.create(params, S3_PRESIGNED_UPLOAD_URL);
        if (response?.data?.status === "success") {
            return response.data.data;
        }
        throw new Error(response?.data?.message || "Failed to get presigned upload URL");
    };

    /**
     * Upload file/blob directly to S3 using the presigned URL (XHR for progress tracking).
     */
    _uploadToS3 = (presignedUrl, fileData, onProgress) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", presignedUrl, true);

            if (onProgress) {
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        const percent = Math.round((event.loaded / event.total) * 100);
                        onProgress(percent);
                    }
                };
            }

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    reject(new Error("S3 upload failed with status " + xhr.status));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during S3 upload"));
            xhr.ontimeout = () => reject(new Error("S3 upload timed out"));

            xhr.send(fileData);
        });
    };

    /**
     * Client-side validation before requesting presigned URL.
     */
    _validateFile = (file, fileName, fileSize, contentType) => {
        if (!file) {
            throw new Error("No file provided");
        }
        const maxBytes = getMaxDocUploadSizeBytes();
        const maxMb = getMaxDocUploadSizeMb();
        if (fileSize > maxBytes) {
            const sizeMB = Math.round(fileSize / (1024 * 1024));
            throw new Error("File size is " + sizeMB + " MB. Maximum allowed is " + maxMb + " MB.");
        }
        if (!ALLOWED_FILE_TYPES.includes(contentType)) {
            throw new Error("File type " + contentType + " is not supported.");
        }
    };
}

export default new S3UploadService();
